import React, { useCallback, useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronDown, TrendingUp, TrendingDown, Minus, Heart, Square, Share2, BarChart2, Volume2, VolumeX } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../store/authStore';
import { useDeviceWebSocket } from '../hooks/useDeviceWebSocket';
import { useHeartbeatSound } from '../hooks/useHeartbeatSound';
import { getSessions, endSession } from '../api/sessions';
export function MonitorScreen() {
  const navigate = useNavigate();
  const {
    token
  } = useAuth();
  const {
    isConnected,
    bpm: rawBpm,
    piezoValue,
    sensor2Value
  } = useDeviceWebSocket(token);
  const {
    soundEnabled,
    toggleSound
  } = useHeartbeatSound(rawBpm, isConnected);
  // Session State
  const [activeSessionId, setActiveSessionId] = useState<string | null>(null);
  const [showEndSession, setShowEndSession] = useState(false);
  const [isEndingSession, setIsEndingSession] = useState(false);
  // Stats State
  const [bpm, setBpm] = useState<number | null>(null);
  const [minBpm, setMinBpm] = useState<number | null>(null);
  const [maxBpm, setMaxBpm] = useState<number | null>(null);
  const [avgBpm, setAvgBpm] = useState<number | null>(null);
  const bpmHistoryRef = useRef<number[]>([]);
  const lastRenderedBpmRef = useRef<number | null>(null);
  const lastBpmUpdateTimeRef = useRef<number>(0);
  // Trend State
  const [trend, setTrend] = useState<'up' | 'down' | 'flat'>('flat');
  // Signal Quality State
  const [signalQuality, setSignalQuality] = useState<'good' | 'fair' | 'poor' | 'none'>('none');
  const recentPiezoRef = useRef<number[]>([]);
  // Baby Activity State
  const [kickCount, setKickCount] = useState(0);
  const [showKick, setShowKick] = useState(false);
  const [showKickText, setShowKickText] = useState(false);
  const lastKickTimeRef = useRef(0);
  // Canvas Refs
  const mainCanvasRef = useRef<HTMLCanvasElement>(null);
  const miniCanvasRef = useRef<HTMLCanvasElement>(null);
  const animationRef = useRef<number>();
  // Data Buffers
  const mainBufferRef = useRef<number[]>(Array(400).fill(0));
  const miniBufferRef = useRef<number[]>(Array(100).fill(0));
  const scrollOffsetRef = useRef<number>(0);
  // Landscape State
  const [isLandscape, setIsLandscape] = useState(false);
  const [showLandscapeStats, setShowLandscapeStats] = useState(true);
  // --- Session Management ---
  useEffect(() => {
    if (!token) return;
    const checkSession = async () => {
      try {
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        const sessions = await getSessions(token, {
          from: startOfToday.toISOString()
        });
        const active = sessions.find((s: any) => s.sessionStatus === 'active');
        if (active) {
          setActiveSessionId(active.id);
        }
      } catch (err) {
        // console.error('Failed to check active session', err)
      }
    };
    checkSession();
  }, [token]);
  const handleEndSession = async () => {
    if (!token || !activeSessionId) return;
    setIsEndingSession(true);
    try {
      await endSession(token, activeSessionId);
      setShowEndSession(false);
      navigate('/app/history');
    } catch (err) {
      // console.error('Failed to end session', err)
      setIsEndingSession(false);
    }
  };
  // --- Orientation Handling ---
  useEffect(() => {
    const handleResize = () => {
      setIsLandscape(window.innerWidth > window.innerHeight && window.innerWidth < 1024);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  // --- Data Processing ---
  useEffect(() => {
    if (!isConnected) {
      setSignalQuality('none');
      return;
    }
    // Update Main Buffer
    mainBufferRef.current.push(piezoValue);
    if (mainBufferRef.current.length > 400) mainBufferRef.current.shift();
    // Update Mini Buffer
    miniBufferRef.current.push(sensor2Value);
    if (miniBufferRef.current.length > 100) miniBufferRef.current.shift();
    // Calculate Signal Quality
    recentPiezoRef.current.push(piezoValue);
    if (recentPiezoRef.current.length > 20) recentPiezoRef.current.shift();
    if (recentPiezoRef.current.length === 20) {
      const min = Math.min(...recentPiezoRef.current);
      const max = Math.max(...recentPiezoRef.current);
      const range = max - min;
      if (range > 200 && range < 3000) setSignalQuality('good');else if (range >= 50 && range <= 200) setSignalQuality('fair');else setSignalQuality('poor');
    }
    // Kick Detection
    if (isConnected && piezoValue > 2000) {
      const now = Date.now();
      if (now - lastKickTimeRef.current > 1000) {
        lastKickTimeRef.current = now;
        setKickCount(prev => prev + 1);
        setShowKick(true);
        setShowKickText(true);
        setTimeout(() => setShowKick(false), 300);
        setTimeout(() => setShowKickText(false), 1500);
      }
    }
  }, [piezoValue, sensor2Value, isConnected]);
  // Throttled BPM Updates & Stats
  useEffect(() => {
    if (!rawBpm || !isConnected) return;
    const now = Date.now();
    const timeSinceLastUpdate = now - lastBpmUpdateTimeRef.current;
    const valueDiff = lastRenderedBpmRef.current ? Math.abs(rawBpm - lastRenderedBpmRef.current) : 100;
    if (valueDiff > 1 || timeSinceLastUpdate > 500) {
      setBpm(rawBpm);
      lastRenderedBpmRef.current = rawBpm;
      lastBpmUpdateTimeRef.current = now;
      // Update Stats
      setMinBpm(prev => prev === null ? rawBpm : Math.min(prev, rawBpm));
      setMaxBpm(prev => prev === null ? rawBpm : Math.max(prev, rawBpm));
      bpmHistoryRef.current.push(rawBpm);
      const sum = bpmHistoryRef.current.reduce((a, b) => a + b, 0);
      setAvgBpm(Math.round(sum / bpmHistoryRef.current.length));
      // Update Trend
      const history = bpmHistoryRef.current;
      if (history.length >= 5) {
        const recent = history.slice(-5);
        let increasing = 0;
        let decreasing = 0;
        for (let i = 1; i < recent.length; i++) {
          if (recent[i] > recent[i - 1]) increasing++;
          if (recent[i] < recent[i - 1]) decreasing++;
        }
        if (increasing >= 3) setTrend('up');else if (decreasing >= 3) setTrend('down');else setTrend('flat');
      }
    }
  }, [rawBpm, isConnected]);
  // --- Beating Heart Animation ---
  const [beatPulse, setBeatPulse] = useState(false);
  useEffect(() => {
    if (!isConnected || !bpm) return;
    const intervalMs = 60000 / bpm;
    const interval = setInterval(() => {
      setBeatPulse(true);
      setTimeout(() => setBeatPulse(false), 400); // Reset after animation
    }, intervalMs);
    return () => clearInterval(interval);
  }, [bpm, isConnected]);
  // --- Canvas Rendering ---
  useEffect(() => {
    const mainCanvas = mainCanvasRef.current;
    const miniCanvas = miniCanvasRef.current;
    if (!mainCanvas || !miniCanvas) return;
    const mainCtx = mainCanvas.getContext('2d');
    const miniCtx = miniCanvas.getContext('2d');
    if (!mainCtx || !miniCtx) return;
    let time = 0;
    const draw = () => {
      // Handle Retina Display
      const dpr = window.devicePixelRatio || 1;
      // Main Canvas Setup
      const mainRect = mainCanvas.parentElement?.getBoundingClientRect();
      if (mainRect) {
        if (mainCanvas.width !== mainRect.width * dpr || mainCanvas.height !== mainRect.height * dpr) {
          mainCanvas.width = mainRect.width * dpr;
          mainCanvas.height = mainRect.height * dpr;
        }
      }
      // Mini Canvas Setup
      const miniRect = miniCanvas.parentElement?.getBoundingClientRect();
      if (miniRect) {
        if (miniCanvas.width !== miniRect.width * dpr || miniCanvas.height !== miniRect.height * dpr) {
          miniCanvas.width = miniRect.width * dpr;
          miniCanvas.height = miniRect.height * dpr;
        }
      }
      const mWidth = mainCanvas.width / dpr;
      const mHeight = mainCanvas.height / dpr;
      // Clear Main Canvas
      mainCtx.save();
      mainCtx.scale(dpr, dpr);
      mainCtx.fillStyle = '#1A1211';
      mainCtx.fillRect(0, 0, mWidth, mHeight);
      // Draw Grid
      mainCtx.strokeStyle = 'rgba(255,255,255,0.04)';
      mainCtx.lineWidth = 1;
      mainCtx.beginPath();
      // Horizontals
      for (let i = 1; i < 5; i++) {
        const y = mHeight / 5 * i;
        mainCtx.moveTo(0, y);
        mainCtx.lineTo(mWidth, y);
      }
      // Verticals
      const scrollMod = scrollOffsetRef.current % 50;
      for (let x = -scrollMod; x < mWidth; x += 50) {
        mainCtx.moveTo(x, 0);
        mainCtx.lineTo(x, mHeight);
      }
      mainCtx.stroke();
      if (!isConnected) {
        // Disconnected State
        time += 0.02;
        mainCtx.beginPath();
        const yCenter = mHeight / 2;
        for (let x = 0; x < mWidth; x++) {
          // Cosmetic pulse traveling right to left
          const pulseX = mWidth - time * 100 % (mWidth * 1.5);
          const dist = Math.abs(x - pulseX);
          const pulseY = dist < 50 ? Math.cos(dist * Math.PI / 100) * 30 : 0;
          if (x === 0) mainCtx.moveTo(x, yCenter - pulseY);else mainCtx.lineTo(x, yCenter - pulseY);
        }
        mainCtx.strokeStyle = 'rgba(107, 33, 232, 0.3)';
        mainCtx.lineWidth = 2;
        mainCtx.stroke();
      } else {
        // Connected State - Draw Waveform
        const data = mainBufferRef.current;
        const step = mWidth / (data.length - 1);
        const paddingY = mHeight * 0.1;
        const usableHeight = mHeight - paddingY * 2;
        const drawLine = (lineWidth: number, color: string) => {
          mainCtx.beginPath();
          for (let i = 0; i < data.length; i++) {
            // Smooth scrolling offset
            const x = i * step - scrollOffsetRef.current % step;
            const y = mHeight - paddingY - data[i] / 4095 * usableHeight;
            if (i === 0) mainCtx.moveTo(x, y);else mainCtx.lineTo(x, y);
          }
          mainCtx.strokeStyle = color;
          mainCtx.lineWidth = lineWidth;
          mainCtx.lineJoin = 'round';
          mainCtx.stroke();
        };
        // Triple Glow
        drawLine(8, 'rgba(107, 33, 232, 0.08)');
        drawLine(4, 'rgba(107, 33, 232, 0.25)');
        drawLine(1.5, '#6B21E8');
        // Leading Dot
        const lastX = (data.length - 1) * step - scrollOffsetRef.current % step;
        const lastY = mHeight - paddingY - data[data.length - 1] / 4095 * usableHeight;
        mainCtx.beginPath();
        mainCtx.arc(lastX, lastY, 12, 0, Math.PI * 2);
        mainCtx.fillStyle = 'rgba(107, 33, 232, 0.4)';
        mainCtx.fill();
        mainCtx.beginPath();
        mainCtx.arc(lastX, lastY, 6, 0, Math.PI * 2);
        mainCtx.fillStyle = '#6B21E8';
        mainCtx.fill();
        // Increment scroll
        scrollOffsetRef.current += 1;
      }
      mainCtx.restore();
      // --- Mini Canvas (Uterine Activity) ---
      const miniW = miniCanvas.width / dpr;
      const miniH = miniCanvas.height / dpr;
      miniCtx.save();
      miniCtx.scale(dpr, dpr);
      miniCtx.clearRect(0, 0, miniW, miniH);
      if (isConnected) {
        const miniData = miniBufferRef.current;
        const miniStep = miniW / (miniData.length - 1);
        miniCtx.beginPath();
        for (let i = 0; i < miniData.length; i++) {
          const x = i * miniStep;
          const y = miniH - miniData[i] / 4095 * miniH;
          if (i === 0) miniCtx.moveTo(x, y);else miniCtx.lineTo(x, y);
        }
        miniCtx.strokeStyle = '#00D4D4';
        miniCtx.lineWidth = 1.5;
        miniCtx.lineJoin = 'round';
        miniCtx.stroke();
      }
      miniCtx.restore();
      animationRef.current = requestAnimationFrame(draw);
    };
    draw();
    return () => {
      if (animationRef.current) cancelAnimationFrame(animationRef.current);
    };
  }, [isConnected]);
  // --- Render Helpers ---
  const getSignalQualityProps = () => {
    switch (signalQuality) {
      case 'good':
        return {
          color: '#00D4D4',
          width: '100%',
          label: 'Good'
        };
      case 'fair':
        return {
          color: '#F59E0B',
          width: '60%',
          label: 'Fair'
        };
      case 'poor':
        return {
          color: '#E8735A',
          width: '30%',
          label: 'Poor'
        };
      default:
        return {
          color: '#8A8A8A',
          width: '0%',
          label: 'No Signal'
        };
    }
  };
  const sqProps = getSignalQualityProps();
  return <main className="fixed inset-0 bg-[#1A1211] overflow-hidden flex flex-col lg:flex-row z-[100]" data-id="element-1440">
      {/* TOP BAR (Hidden in landscape mobile) */}
      {!isLandscape && <div className="absolute top-0 left-0 right-0 h-[80px] pt-[env(safe-area-inset-top)] px-4 flex items-center justify-between z-20 bg-gradient-to-b from-[#1A1211]/80 to-transparent" data-id="element-1441">
          <button onClick={() => navigate('/app/dashboard')} className="w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" aria-label="Go back" data-id="element-1442">
            <ChevronDown size={24} className="text-white/80" data-id="element-1443" />
          </button>
          <span className="text-white text-[16px] font-semibold" data-id="element-1444">
            Live Monitor
          </span>
          <div className="w-[100px] flex justify-end items-center space-x-3" data-id="element-1445">
            <button onClick={toggleSound} className="w-[40px] h-[40px] rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20 transition-colors" aria-label={soundEnabled ? 'Mute heartbeat' : 'Play heartbeat'} data-id="element-1446">
              {soundEnabled ? <Volume2 size={20} className="text-accent" data-id="element-1447" /> : <VolumeX size={20} className="text-white/80" data-id="element-1448" />}
            </button>
            {activeSessionId && <div className="flex items-center space-x-2" data-id="element-1449">
                <div className="w-2 h-2 rounded-full bg-danger animate-pulse" data-id="element-1450" />
                <span className="text-white/80 text-[13px] hidden sm:inline" data-id="element-1451">
                  Recording
                </span>
              </div>}
          </div>
        </div>}

      {/* LEFT PANEL / MAIN CANVAS */}
      <div className={`relative w-full ${isLandscape ? 'h-full' : 'h-[55vh] lg:h-full lg:w-[65%]'} flex-shrink-0`} onClick={() => isLandscape && setShowLandscapeStats(!showLandscapeStats)} data-id="element-1452">
        <div className="absolute inset-0 w-full h-full" data-id="element-1453">
          <canvas ref={mainCanvasRef} className="w-full h-full block" role="img" aria-label="Live fetal heart rate waveform" data-id="element-1454" />
        </div>

        {/* Gradient Overlay for mobile/tablet portrait */}
        {!isLandscape && <div className="absolute bottom-0 left-0 right-0 h-[100px] bg-gradient-to-b from-transparent to-[#1A1211] lg:hidden" data-id="element-1455" />}
      </div>

      {/* RIGHT PANEL / STATS & CONTROLS */}
      {(!isLandscape || showLandscapeStats) && <div className={`
          ${isLandscape ? 'absolute bottom-4 right-4 bg-[#221A19]/90 backdrop-blur-md rounded-[24px] p-4 flex flex-row items-center space-x-6' : 'flex-1 flex flex-col bg-[#1A1211] lg:bg-[#221A19] z-10 px-6 pb-6 lg:p-8 overflow-y-auto'}
        `} data-id="element-1456">
          {/* BPM Hero Display */}
          <div className={`flex flex-col ${isLandscape ? 'items-start' : 'items-center lg:items-start lg:mt-12'}`} data-id="element-1457">
            <div className="flex items-start" data-id="element-1458">
              <motion.div key={bpm} initial={{
            y: -4,
            scale: 1.05
          }} animate={{
            y: 0,
            scale: 1
          }} transition={{
            duration: 0.25
          }} className={`font-bold text-primary leading-none ${isLandscape ? 'text-[48px]' : 'text-[72px] lg:text-[96px]'}`} aria-live="polite" aria-label={`Current BPM: ${isConnected && bpm ? bpm : 'Unknown'}`} data-id="element-1459">
                {isConnected && bpm ? bpm : '--'}
              </motion.div>
              <div className="ml-2 mt-2 flex flex-col items-center" data-id="element-1460">
                {trend === 'up' && <TrendingUp size={24} className="text-accent mb-1" data-id="element-1461" />}
                {trend === 'down' && <TrendingDown size={24} className="text-danger mb-1" data-id="element-1462" />}
                {trend === 'flat' && <Minus size={24} className="text-white/40 mb-1" data-id="element-1463" />}
              </div>
            </div>
            <span className="text-white/50 text-[16px] mt-1" data-id="element-1464">BPM</span>

            {/* Beating Heart */}
            {!isLandscape && <div className="relative mt-6 mb-8 flex items-center justify-center lg:justify-start w-full" data-id="element-1465">
                <motion.div animate={beatPulse ? {
            scale: [1, 1.3, 1]
          } : {
            scale: 1
          }} transition={{
            duration: 0.2,
            ease: 'easeInOut'
          }} className={`relative z-10 ${!isConnected ? 'opacity-40' : ''}`} data-id="element-1466">
                  <Heart size={40} className="text-primary fill-primary" data-id="element-1467" />
                </motion.div>

                {/* Ripple */}
                {beatPulse && isConnected && <motion.div initial={{
            scale: 1,
            opacity: 0.5
          }} animate={{
            scale: 2.5,
            opacity: 0
          }} transition={{
            duration: 0.4
          }} className="absolute w-[40px] h-[40px] rounded-full border-2 border-primary lg:ml-0" data-id="element-1468" />}

                {!isConnected && <div className="absolute top-12 flex items-center space-x-2" data-id="element-1469">
                    <div className="w-4 h-4 border-2 border-primary border-t-transparent rounded-full animate-spin" data-id="element-1470" />
                    <span className="text-white/50 text-[13px]" data-id="element-1471">
                      Reconnecting...
                    </span>
                  </div>}
              </div>}
          </div>

          {/* Stats Row */}
          <div className={`flex ${isLandscape ? 'space-x-6 ml-6 border-l border-white/10 pl-6' : 'justify-between w-full lg:flex-col lg:space-y-6 lg:mt-8'}`} data-id="element-1472">
            <div className="flex flex-col" data-id="element-1473">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1474">
                Min
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1475">
                {minBpm || '--'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-1476">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1477">
                Max
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1478">
                {maxBpm || '--'}
              </span>
            </div>
            <div className="flex flex-col" data-id="element-1479">
              <span className="text-[12px] text-white/50 uppercase tracking-[1px] mb-1" data-id="element-1480">
                Avg
              </span>
              <span className="text-[20px] font-semibold text-white" data-id="element-1481">
                {avgBpm || '--'}
              </span>
            </div>
          </div>

          {/* Signal Quality & Uterine Activity (Hidden in landscape) */}
          {!isLandscape && <div className="w-full mt-8 lg:mt-12 space-y-8" data-id="element-1482">
              {/* Signal Quality */}
              <div data-id="element-1483">
                <span className="text-[12px] text-white/50 uppercase tracking-[1px] block mb-2" data-id="element-1484">
                  Signal Quality
                </span>
                <div className="w-full h-[6px] bg-white/10 rounded-pill overflow-hidden mb-2" data-id="element-1485">
                  <div className="h-full rounded-pill transition-all duration-500" style={{
              width: sqProps.width,
              backgroundColor: sqProps.color
            }} data-id="element-1486" />
                </div>
                <span className="text-[13px]" style={{
            color: sqProps.color
          }} data-id="element-1487">
                  {sqProps.label}
                </span>
              </div>

              {/* Uterine Activity */}
              <div data-id="element-1488">
                <span className="text-[12px] text-white/50 uppercase tracking-[1px] block mb-2" data-id="element-1489">
                  Uterine Activity
                </span>
                <div className="w-full h-[40px] relative mb-1" data-id="element-1490">
                  <canvas ref={miniCanvasRef} className="absolute inset-0 w-full h-full" role="img" aria-label="Live uterine activity waveform" data-id="element-1491" />
                </div>
                <span className="text-[14px] text-white/60" data-id="element-1492">
                  {isConnected ? sensor2Value : '--'}
                </span>
              </div>

              {/* Baby Activity */}
              <div className="bg-white/5 rounded-[16px] p-4 relative overflow-hidden" data-id="element-1493">
                <div className="flex justify-between items-center mb-4" data-id="element-1494">
                  <span className="text-[12px] text-white/50 uppercase tracking-[1px]" data-id="element-1495">
                    Baby Activity 👶
                  </span>
                  <span className="text-[12px] text-white/50" data-id="element-1496">
                    Kicks this session:{' '}
                    <span className="text-white font-medium" data-id="element-1497">{kickCount}</span>
                  </span>
                </div>

                <div className="flex justify-center items-center h-[100px] relative" data-id="element-1498">
                  <AnimatePresence data-id="element-1499">
                    {showKickText && <motion.div initial={{
                opacity: 0,
                y: 10,
                x: 20
              }} animate={{
                opacity: 1,
                y: -20,
                x: 20
              }} exit={{
                opacity: 0
              }} className="absolute text-accent font-bold text-[14px] z-10" data-id="element-1500">
                        +kick
                      </motion.div>}
                  </AnimatePresence>

                  <motion.div animate={!isConnected ? {
              scale: 1,
              opacity: 0.4
            } : showKick ? {
              scale: 1.05,
              x: 4,
              rotate: 3
            } : {
              scale: [1, 1.03, 1]
            }} transition={showKick ? {
              duration: 0.15,
              yoyo: 1
            } : {
              duration: 3,
              repeat: Infinity,
              ease: 'easeInOut'
            }} className="relative" data-id="element-1501">
                    <svg width="80" height="80" viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-1502">
                      {/* Stylized baby silhouette */}
                      <path d="M50 20C40 20 32 28 32 38C32 42 33.5 45.5 36 48.5C30 52 25 58 25 65C25 76 36 85 50 85C64 85 75 76 75 65C75 58 70 52 64 48.5C66.5 45.5 68 42 68 38C68 28 60 20 50 20Z" fill="#6B21E8" opacity="0.8" data-id="element-1503" />
                      <circle cx="45" cy="35" r="3" fill="white" opacity="0.6" data-id="element-1504" />
                      <path d="M40 42Q45 45 50 42" stroke="white" strokeWidth="2" strokeLinecap="round" opacity="0.6" data-id="element-1505" />
                      <path d="M35 65Q50 75 65 65" stroke="#00D4D4" strokeWidth="3" strokeLinecap="round" opacity="0.8" data-id="element-1506" />
                    </svg>
                  </motion.div>
                </div>
              </div>
            </div>}

          {/* Bottom Controls */}
          {!isLandscape && <div className="mt-auto pt-6 lg:pt-12 w-full flex space-x-3 pb-[env(safe-area-inset-bottom)]" data-id="element-1507">
              <button onClick={() => setShowEndSession(true)} className="flex-1 h-[52px] bg-danger/20 border border-danger text-danger rounded-[14px] flex items-center justify-center space-x-2 font-medium" data-id="element-1508">
                <Square size={18} className="fill-danger" data-id="element-1509" />
                <span data-id="element-1510">End Session</span>
              </button>
              <button className="w-[52px] h-[52px] bg-white/10 border border-white/20 text-white/80 rounded-[14px] flex items-center justify-center" aria-label="Share" data-id="element-1511">
                <Share2 size={20} data-id="element-1512" />
              </button>
              <button onClick={() => navigate('/app/history')} className="w-[52px] h-[52px] bg-white/10 border border-white/20 text-white/80 rounded-[14px] flex items-center justify-center" aria-label="View history" data-id="element-1513">
                <BarChart2 size={20} data-id="element-1514" />
              </button>
            </div>}
        </div>}

      {/* END SESSION BOTTOM SHEET */}
      <AnimatePresence data-id="element-1515">
        {showEndSession && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} onClick={() => setShowEndSession(false)} className="fixed inset-0 bg-black/60 z-[110]" data-id="element-1516" />
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 25,
          stiffness: 200
        }} className="fixed bottom-0 left-0 right-0 bg-[#221A19] rounded-t-[24px] p-6 pb-[calc(24px+env(safe-area-inset-bottom))] z-[120] lg:max-w-[400px] lg:left-1/2 lg:-translate-x-1/2 lg:bottom-6 lg:rounded-[24px]" data-id="element-1517">
              <div className="w-[40px] h-[4px] bg-white/20 rounded-pill mx-auto mb-6 lg:hidden" data-id="element-1518" />
              <h2 className="text-[20px] font-semibold text-white mb-2" data-id="element-1519">
                End this session?
              </h2>
              <p className="text-[15px] text-white/60 mb-8" data-id="element-1520">
                Your session data will be saved and synced. You can review it in
                History.
              </p>
              <div className="flex flex-col space-y-3" data-id="element-1521">
                <button onClick={handleEndSession} disabled={isEndingSession} className="w-full h-[52px] bg-danger text-white rounded-[14px] font-medium flex items-center justify-center" data-id="element-1522">
                  {isEndingSession ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="element-1523" /> : 'Yes, End Session'}
                </button>
                <button onClick={() => setShowEndSession(false)} disabled={isEndingSession} className="w-full h-[52px] bg-transparent text-white/70 rounded-[14px] font-medium" data-id="element-1524">
                  Keep Monitoring
                </button>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </main>;
}